import * as React from 'react';
import { Box, ClickAwayListener } from '@mui/material';
import VolumeUpRounded from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRounded from '@mui/icons-material/VolumeOffRounded';
import VolumeControls from './VolumeControls';
import { volumeControlsAvailable } from './volumeAvailability';
import { useNarrationGain } from './useNarrationGain';
import { useHardwareBackClosesLayer } from 'Hooks/useHardwareBack';
import { TOUCH_TARGET_MIN_SIZE_PX } from 'Utils/touchTarget';

// The speaker button with a mouse: a 20px icon in 1px / 8px of padding and a 1px border.
const VOLUME_ICON_SIZE_PX = 20;
const VOLUME_BUTTON_PADDING_Y_PX = 1;
const VOLUME_BUTTON_PADDING_X_PX = 8;
const volumeButtonHeightPx = VOLUME_ICON_SIZE_PX + 2 * (VOLUME_BUTTON_PADDING_Y_PX + 1);
const volumeButtonWidthPx = VOLUME_ICON_SIZE_PX + 2 * (VOLUME_BUTTON_PADDING_X_PX + 1);
const touchBorderY = (TOUCH_TARGET_MIN_SIZE_PX - volumeButtonHeightPx) / 2;
const touchBorderX = (TOUCH_TARGET_MIN_SIZE_PX - volumeButtonWidthPx) / 2;

/**
 * A speaker button that opens the volume controls above it, for play surfaces
 * with no settings panel of their own (the tutorial video). The panel renders
 * in place rather than in a portal so it stays visible in fullscreen.
 */
export default function VolumeMenuButton() {
  const [open, setOpen] = React.useState(false);
  // The icon shows whether the video's own sound, the narration, is audible.
  const narrationSilent = useNarrationGain() === 0;
  // Android's Back closes the controls as a tap outside them does.
  // Drawn in place, in the play surface's controls.
  useHardwareBackClosesLayer(open, () => setOpen(false), 0);
  // Escape closes the open controls and does nothing else: it must not also
  // reach the play surface's own Escape (pause), so it stops here, first.
  React.useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // A dialog or menu opened above the panel takes its own Escape first.
      if (document.querySelector('[role="dialog"], [role="menu"], .MuiModal-root')) return;
      event.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape, true);
    return () => window.removeEventListener('keydown', closeOnEscape, true);
  }, [open]);

  if (!volumeControlsAvailable()) return null;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        sx={{ position: 'relative', display: 'flex' }}
        // Space / Enter on these buttons press them; keep the key from also
        // reaching the play surface's own Space / Enter (pause) handler.
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === ' ' || event.key === 'Enter') event.stopPropagation();
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label="Volume"
          aria-expanded={open}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            background: '#525969',
            color: 'white',
            padding: `${VOLUME_BUTTON_PADDING_Y_PX}px ${VOLUME_BUTTON_PADDING_X_PX}px`,
            border: '1px solid #525969',
            cursor: 'pointer',
            ':hover': {
              border: '1px solid white',
              background: '#333B4D',
            },
            // A 44px target on a touch screen that looks the same: a transparent border fills the
            // target out around the grey box (drawn in the padding, its edge an inset shadow), and
            // the negative margin gives the extra back, so the video's control bar keeps its size
            // and the panel opens where it did.
            '@media (pointer: coarse)': {
              paddingBlock: `${VOLUME_BUTTON_PADDING_Y_PX + 1}px`,
              paddingInline: `${VOLUME_BUTTON_PADDING_X_PX + 1}px`,
              borderStyle: 'solid',
              borderColor: 'transparent',
              borderBlockWidth: `${touchBorderY}px`,
              borderInlineWidth: `${touchBorderX}px`,
              backgroundClip: 'padding-box',
              boxShadow: 'inset 0 0 0 1px #525969',
              marginBlock: `${-touchBorderY}px`,
              marginInline: `${-touchBorderX}px`,
              ':hover': {
                borderStyle: 'solid',
                borderColor: 'transparent',
                borderBlockWidth: `${touchBorderY}px`,
                borderInlineWidth: `${touchBorderX}px`,
                // The hover background shorthand resets the clip: keep the grey box its own size.
                backgroundClip: 'padding-box',
                boxShadow: 'inset 0 0 0 1px white',
              },
            },
          }}
        >
          {narrationSilent ? (
            <VolumeOffRounded fontSize="small" />
          ) : (
            <VolumeUpRounded fontSize="small" />
          )}
        </Box>
        {/* After the button in the page order, so Tab from the button moves into
            the open panel; it is drawn above the button all the same. */}
        {open && (
          <Box
            data-control-panel
            sx={{
              position: 'absolute',
              bottom: 'calc(100% + 10px)',
              right: 0,
              width: '330px',
              padding: '14px 18px',
              borderRadius: '8px',
              backgroundColor: '#050B13',
              boxShadow: '0px 12px 26px rgba(0, 0, 0, 0.38)',
            }}
          >
            <VolumeControls title="Volume" />
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
}
